import type { EmbedErrorDetail } from './embed-widget.types'
import { ensureResourcesLoaded, getEmbedGlobal } from './resource-loader'

/**
 * `<gs-cc-embed-widget>` — Orchestrator for embedding community widgets on external sites.
 *
 * Loads shared resources (Preact runtime, CSS variables, CSRF token), fetches
 * widget HTML from the backend, and injects it into its light DOM. The response
 * contains a `<gs-cc-widget>` which handles shadow DOM projection, design token
 * injection, and Preact hydration independently.
 *
 * @fires embed-loading — Fetch started
 * @fires embed-loaded  — Widget HTML injected into DOM
 * @fires widget-ready  — Inner widget connected and hydrated
 * @fires embed-error   — Loading or binding failed
 */
export class EmbedWidget extends HTMLElement {
  private abortController: AbortController | null = null

  /**
   * Attributes that trigger a widget re-fetch when changed.
   * Must be lowercase — HTML `setAttribute` lowercases names per spec.
   */
  static get observedAttributes(): string[] {
    return ['page', 'type']
  }

  attributeChangedCallback(_name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return
    if (this.isConnected) {
      this.loadWidget()
    }
  }

  connectedCallback(): void {
    this.loadWidget()
  }

  disconnectedCallback(): void {
    this.abort()
    this.innerHTML = ''
  }

  private get page(): string {
    return this.getAttribute('page') ?? ''
  }

  private get widgetType(): string {
    return this.getAttribute('type') ?? ''
  }

  private buildRequestBody(): Record<string, unknown> {
    const global = getEmbedGlobal()
    const body: Record<string, unknown> = {
      page: this.page,
      type: this.widgetType
    }

    body.context = {
      communityUrl: global.communityUrl,
      productType: global.productType,
      language: global.language,
      ...global.context
    }

    return body
  }

  private abort(): void {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }

  private async loadWidget(): Promise<void> {
    const communityUrl = getEmbedGlobal().communityUrl
    const { page, widgetType } = this

    if (!communityUrl) {
      this.emitError('window.__insidedEmbed.communityUrl is required')
      return
    }

    if (!page || !widgetType) {
      return
    }

    this.abort()
    this.abortController = new AbortController()
    const { signal } = this.abortController

    this.dispatch('embed-loading')

    try {
      await ensureResourcesLoaded(communityUrl)

      if (signal.aborted) return

      const csrfToken = getEmbedGlobal().csrfToken
      if (!csrfToken) {
        this.emitError('CSRF token not available — /widget/resources may not have loaded correctly')
        return
      }

      const headers: Record<string, string> = {
        Accept: 'text/html',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-TOKEN': csrfToken
      }

      const response = await fetch(`${communityUrl}/widget/embed`, {
        method: 'POST',
        headers,
        body: JSON.stringify(this.buildRequestBody()),
        signal
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch widget: ${response.status}`)
      }

      const html = await response.text()

      if (signal.aborted) return

      this.innerHTML = html
      this.dispatch('embed-loaded')

      // The inner <gs-cc-widget> handles shadow DOM projection, design tokens,
      // and Preact hydration via its own connectedCallback.
      const widget = this.querySelector('gs-cc-widget')
      if (widget?.shadowRoot) {
        this.dispatch('widget-ready')
      }
    } catch (error) {
      if (signal.aborted) return

      this.handleLoadError(error)
    }
  }

  private handleLoadError(error: unknown): void {
    const message = error instanceof Error ? error.message : String(error)
    const errorObj = error instanceof Error ? error : undefined
    this.emitError(message, errorObj)
  }

  private dispatch(type: string): void {
    this.dispatchEvent(new CustomEvent(type, { bubbles: true, composed: true }))
  }

  private emitError(message: string, error?: Error): void {
    console.error(`[EmbedWidget] ${message}`, error)
    this.dispatchEvent(
      new CustomEvent<EmbedErrorDetail>('embed-error', {
        bubbles: true,
        composed: true,
        detail: { message, error }
      })
    )
  }
}

if (typeof customElements !== 'undefined') {
  if (!customElements.get('gs-cc-embed-widget')) {
    customElements.define('gs-cc-embed-widget', EmbedWidget)
  }
}
